import { format } from "date-fns";
import { ArrowUpRight, DollarSign } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface Transaction {
  id: string;
  created_at: string;
  transaction_type: string;
  withdrawal_method?: string;
  status: string;
  amount: number;
}

interface TransactionTableProps {
  transactions: Transaction[] | null;
  isLoading: boolean;
}

export const TransactionTable = ({ transactions, isLoading }: TransactionTableProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Details</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions?.map((transaction) => (
          <TableRow key={transaction.id}>
            <TableCell>
              {format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm')}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {transaction.transaction_type === 'withdrawal' ? (
                  <ArrowUpRight className="h-4 w-4 text-red-500" />
                ) : (
                  <DollarSign className="h-4 w-4 text-green-500" />
                )}
                <span className="capitalize">{transaction.transaction_type}</span>
              </div>
            </TableCell>
            <TableCell>
              {transaction.withdrawal_method && (
                <span className="text-sm text-gray-500">
                  via {transaction.withdrawal_method.split('_').map(
                    word => word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </span>
              )}
            </TableCell>
            <TableCell>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                transaction.status === 'completed' 
                  ? 'bg-green-100 text-green-800'
                  : transaction.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {transaction.status.charAt(0).toUpperCase() + 
                 transaction.status.slice(1)}
              </span>
            </TableCell>
            <TableCell className={`text-right font-medium ${
              transaction.amount < 0 ? 'text-red-500' : 'text-green-500'
            }`}>
              ${Math.abs(transaction.amount).toFixed(2)}
            </TableCell>
          </TableRow>
        ))}
        {(!transactions || transactions.length === 0) && (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
              No transactions found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};